'use client'

import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { REDEEM_NS, redeemFallbackEn, type RedeemMessageKey } from './redeem.en'

type Values = Record<string, string | number | Date>

/**
 * CMS value first, bundled English for keys the CMS lacks.
 * Reads the ROOT namespace and a fully-qualified key: scoping to `Redeem Gift` throws when the
 * namespace is absent, which is the case this hook exists to survive.
 * Collapses to `useTranslations(REDEEM_NS)` once the CMS has all 7 locales.
 */
export function useRedeemT() {
  const t = useTranslations()

  return useCallback(
    (key: RedeemMessageKey, values?: Values): string => {
      const path = `${REDEEM_NS}.${key}`

      try {
        if (t.has(path)) return t(path, values)
      } catch {
        // A malformed CMS entry (bad ICU syntax, wrong placeholders) must not blank the page.
        console.warn(`[redeem] CMS message "${path}" failed to format; using fallback`)
      }

      return formatFallback(redeemFallbackEn[key], values)
    },
    [t]
  )
}

/**
 * Minimal ICU subset for fallbacks: `{name}` and `{n, plural, one {…} other {…}}`.
 * Small on purpose — next-intl does the real formatting whenever a CMS value exists.
 */
function formatFallback(template: string, values?: Values): string {
  if (!values) return template

  return template
    .replace(
      /\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\}/g,
      (_, name: string, one: string, other: string) => {
        const raw = values[name]
        const count = typeof raw === 'number' ? raw : Number(raw)
        const chosen = count === 1 ? one : other

        return chosen.replace(/#/g, String(raw ?? ''))
      }
    )
    .replace(/\{(\w+)\}/g, (match, name: string) => (name in values ? String(values[name]) : match))
}
